import { BaseDevice } from '../../../runtime/BaseDevice'
import type { HidChannel } from '../../../channels/hid/HidChannel'
import { DeviceManifest } from '../../../types/manifest'
import { OHIDKeyboardLighting } from '../../../chips/ohid/feature-impls/keyboard/keyboard-lighting'
import { OHIDKeyboardKeys } from '../../../chips/ohid/feature-impls/keyboard/keyboard-keys'
import { OHIDTravel } from '../../../chips/ohid/feature-impls/keyboard/key-travel'
import { OHIDFirmware } from '../../../chips/ohid/feature-impls/keyboard/firmware'
/**
 * OHID 系列设备驱动实现,OpenAgreementHID 简称 OHID，为基于键盘应用而设计的一款开源通信协议
 *
 * 这是一个 "容器类"，负责组装各个功能模块
 */
export class OHIDDevice extends BaseDevice {
  // 当前CH585M系列驱动sdk版本号
  public readonly sdkVersion = '0.0.1'
  constructor(channel: HidChannel, manifest: DeviceManifest) {
    super(channel, manifest)

    // 自动检测连接类型
    this.detectConnectionType()

    // 组装具体的功能实现
    // 将 Feature 实例直接挂载到 features 容器中
    this.features.keyboardLighting = new OHIDKeyboardLighting(this)
    this.features.keyboardKeys = new OHIDKeyboardKeys(this)
    this.features.keyTravel = new OHIDTravel(this)
    this.features.firmware = new OHIDFirmware(this)
  }

  /**
   * 检测连接类型 (有线/无线)
   * 策略：
   * 1. 检查 Manifest 定义 (假设第二个 PID 为无线)
   * 2. 检查设备名称是否包含 "Receiver"
   */
  private detectConnectionType() {
    const info = this.channel.deviceInfo
    if (!info) return

    const pids = Array.isArray(this.manifest.hardware.productId)
      ? this.manifest.hardware.productId
      : [this.manifest.hardware.productId]

    let isWireless = false

    // 策略 1: 如果 PID 是列表中的第二个，认为是无线
    if (pids.length > 1 && info.productId === pids[1]) {
      isWireless = true
    }
    // 策略 2: 名称包含 "Receiver" 或 "Dongle"
    else if (info.productName && /Receiver|Dongle/i.test(info.productName)) {
      isWireless = true
    }

    if (isWireless) {
      this.connectionType = 0xa5 // 无线模式
      console.log(`[OHIDDevice] Detected Wireless Mode (PID: 0x${info.productId.toString(16)})`)
    } else {
      this.connectionType = 0xff // 有线模式
      console.log(`[OHIDDevice] Detected Wired Mode (PID: 0x${info.productId.toString(16)})`)
    }
  }

  protected onConnected(): void {
    console.log(`[OHIDDevice] Connected: ${this.manifest.model}`)
    // 初始化读取
    // 当前使用的配置
    this.features.config?.getCurrentConfig().then((config) => {
      this.currentConfig = config
    })
    this.features.dpi?.getCurrentGear().catch((err) => {
      console.error('[OHIDDevice] Failed to get current DPI gear:', err)
    })
  }

  async checkConnect(): Promise<boolean> {
    try {
      const isConnected = this.channel?.isConnected
      if (!isConnected) {
        return false
      }
      await this.features.firmware?.getFirmwareVersion()
      return true
    } catch (_error) {
      return false
    }
  }
}
